import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiBook, FiLogOut, FiMessageSquare, FiSend, FiUser } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Tutoring = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [order, setOrder] = useState('desc');
  const [topics, setTopics] = useState([]);
  const [faqTopics, setFaqTopics] = useState([]);
  const [recordedVideos, setRecordedVideos] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [activeTopic, setActiveTopic] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTopic, setNewTopic] = useState({ title: '', question: '' });
  const [commentText, setCommentText] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page_size: 20 });

  const getYouTubeEmbedUrl = useCallback((url) => {
    if (!url) return '';

    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      const videoId = watchMatch[1].split('&')[0].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
      return `https://www.youtube.com/embed/${shortMatch[1]}`;
    }

    return '';
  }, []);

  const fetchChapters = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tutoring/chapters`);
      setChapters(response.data.chapters || []);
    } catch (err) {
      setError('Erro ao carregar aulas.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTopics = useCallback(async (pageToLoad, reset) => {
    setTopicsLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (selectedChapterId) {
        params.append('chapter_id', selectedChapterId);
      }
      params.append('order', order);
      params.append('page', String(pageToLoad));
      params.append('page_size', '20');
      params.append('exclude_faq', '1');

      const response = await axios.get(`${API_URL}/tutoring/topics?${params.toString()}`);
      const nextTopics = response.data.topics || [];
      const nextPagination = response.data.pagination || { total: 0, page_size: 20 };

      if (reset) {
        setTopics(nextTopics);
      } else {
        setTopics((prev) => [...prev, ...nextTopics]);
      }

      setPagination(nextPagination);
      if (nextTopics.length > 0) {
        setActiveTopicId((current) => current ?? nextTopics[0].id);
      }
    } catch (err) {
      setError('Erro ao carregar topicos.');
    } finally {
      setTopicsLoading(false);
    }
  }, [order, selectedChapterId]);

  const fetchFaqTopics = useCallback(async () => {
    if (!selectedChapterId) {
      setFaqTopics([]);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('chapter_id', selectedChapterId);
      params.append('faq_only', '1');
      params.append('page', '1');
      params.append('page_size', '50');

      const response = await axios.get(`${API_URL}/tutoring/topics?${params.toString()}`);
      setFaqTopics(response.data.topics || []);
    } catch (err) {
      setFaqTopics([]);
    }
  }, [selectedChapterId]);

  const fetchRecordedVideos = useCallback(async () => {
    if (!selectedChapterId) {
      setRecordedVideos([]);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('chapter_id', selectedChapterId);

      const response = await axios.get(`${API_URL}/tutoring/recorded-videos?${params.toString()}`);
      setRecordedVideos(response.data.recordedVideos || []);
    } catch (err) {
      setRecordedVideos([]);
    }
  }, [selectedChapterId]);

  const fetchTopicDetail = useCallback(async (topicId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tutoring/topics/${topicId}`);
      setActiveTopic(response.data.topic);
      setComments(response.data.comments || []);
    } catch (err) {
      setActiveTopic(null);
      setComments([]);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  useEffect(() => {
    setActiveTopicId(null);
    setActiveTopic(null);
    setComments([]);
    setPage(1);
    fetchTopics(1, true);
    fetchFaqTopics();
    fetchRecordedVideos();
  }, [selectedChapterId, order, fetchTopics, fetchFaqTopics, fetchRecordedVideos]);

  useEffect(() => {
    if (activeTopicId) {
      fetchTopicDetail(activeTopicId);
    } else {
      setActiveTopic(null);
      setComments([]);
    }
  }, [activeTopicId, fetchTopicDetail]);

  const handleCreateTopic = async (event) => {
    event.preventDefault();

    if (!selectedChapterId) {
      alert('Selecione uma aula.');
      return;
    }

    if (!newTopic.title.trim() || !newTopic.question.trim()) {
      alert('Preencha titulo e descricao.');
      return;
    }

    try {
      await axios.post(`${API_URL}/tutoring/topics`, {
        chapter_id: parseInt(selectedChapterId, 10),
        title: newTopic.title,
        question: newTopic.question
      });
      setNewTopic({ title: '', question: '' });
      setPage(1);
      await fetchTopics(1, true);
      await fetchFaqTopics();
      await fetchRecordedVideos();
    } catch (err) {
      alert('Erro ao criar topico.');
    }
  };

  const handleCreateComment = async () => {
    if (!activeTopicId || !commentText.trim()) {
      return;
    }

    try {
      await axios.post(`${API_URL}/tutoring/topics/${activeTopicId}/comments`, {
        content: commentText
      });
      setCommentText('');
      fetchTopicDetail(activeTopicId);
    } catch (err) {
      alert('Erro ao criar comentario.');
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTopics(nextPage, false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const chapterOptions = useMemo(() => {
    return chapters.map((chapter) => ({
      value: String(chapter.id),
      label: `${chapter.category_name} - ${chapter.title}`
    }));
  }, [chapters]);

  const hasMore = topics.length < pagination.total;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Tutoria por Aula</h1>
            <p className="text-sm text-gray-600">Tire suas duvidas e aprenda com a comunidade.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiUser className="text-gray-400" />
              <span>{user?.username}</span>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <FiBook />
              <span className="hidden sm:inline">Aulas</span>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <FiUser />
              <span className="hidden sm:inline">Perfil</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <FiLogOut />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          <section className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
                <FiMessageSquare className="text-gray-400" />
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Aula
                  <select
                    value={selectedChapterId}
                    onChange={(event) => setSelectedChapterId(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todas as aulas</option>
                    {chapterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Ordenacao
                  <select
                    value={order}
                    onChange={(event) => setOrder(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="desc">Mais recentes</option>
                    <option value="asc">Mais antigas</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Criar nova duvida</h2>
              <form onSubmit={handleCreateTopic} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Titulo</label>
                  <input
                    type="text"
                    value={newTopic.title}
                    onChange={(event) => setNewTopic({ ...newTopic, title: event.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Duvida sobre loops"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descricao</label>
                  <textarea
                    value={newTopic.question}
                    onChange={(event) => setNewTopic({ ...newTopic, question: event.target.value })}
                    rows={4}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Explique sua duvida aqui..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <FiSend />
                  Enviar duvida
                </button>
              </form>
            </div>

            {selectedChapterId && faqTopics.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">FAQ da Aula</h2>
                <div className="space-y-3">
                  {faqTopics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setActiveTopicId(topic.id)}
                      className={`w-full text-left p-3 rounded-lg border transition ${
                        activeTopicId === topic.id
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-200'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-800">{topic.title}</p>
                      <p className="text-xs text-gray-500">Atualizado em {new Date(topic.created_at).toLocaleString('pt-BR')}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedChapterId && recordedVideos.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Tutorias Gravadas</h2>
                <div className="space-y-4">
                  {recordedVideos.map((video) => {
                    const embedUrl = getYouTubeEmbedUrl(video.video_url);

                    return (
                      <div key={video.id} className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                        <div className="p-3 border-b border-gray-200 bg-white">
                          <p className="text-sm font-semibold text-gray-800">{video.title}</p>
                          <p className="text-xs text-gray-500">{video.category_name} - {video.chapter_title}</p>
                        </div>
                        <div className="relative w-full bg-gray-900" style={{ paddingBottom: '56.25%' }}>
                          {embedUrl ? (
                            <iframe
                              className="absolute top-0 left-0 w-full h-full"
                              src={embedUrl}
                              title={video.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          ) : (
                            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white text-sm">
                              URL do vídeo inválida
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Topicos</h2>
              {topicsLoading && topics.length === 0 ? (
                <p className="text-sm text-gray-500">Carregando topicos...</p>
              ) : topics.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum topico encontrado.</p>
              ) : (
                <div className="space-y-3">
                  {topics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setActiveTopicId(topic.id)}
                      className={`w-full text-left p-3 rounded-lg border transition ${
                        activeTopicId === topic.id
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-800">{topic.title}</p>
                        {topic.answer_text ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Respondido</span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Sem resposta</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{topic.category_name} - {topic.chapter_title}</p>
                      <p className="text-xs text-gray-500">{new Date(topic.created_at).toLocaleString('pt-BR')}</p>
                      <p className="text-xs text-gray-500">{topic.comment_count || 0} comentarios</p>
                    </button>
                  ))}
                  {hasMore && (
                    <button
                      onClick={handleLoadMore}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      Carregar mais
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-6">
            {detailsLoading ? (
              <p className="text-sm text-gray-500">Carregando detalhes...</p>
            ) : !activeTopic ? (
              <div className="text-center text-gray-500">
                <p className="text-sm">Selecione um topico para ver os detalhes.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {activeTopic.is_faq === 1 && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">FAQ</span>
                    )}
                    {activeTopic.answer_text ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Respondido</span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Sem resposta</span>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">{activeTopic.title}</h2>
                  <p className="text-sm text-gray-500">{activeTopic.category_name} - {activeTopic.chapter_title}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Criado em {new Date(activeTopic.created_at).toLocaleString('pt-BR')} por {activeTopic.student_name}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Duvida</h3>
                  <p className="text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-wrap">
                    {activeTopic.question}
                  </p>
                </div>

                {activeTopic.answer_text ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Resposta oficial</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-gray-800 whitespace-pre-wrap">{activeTopic.answer_text}</p>
                      {activeTopic.answered_by_name && (
                        <p className="text-xs text-gray-500 mt-2">Respondido por {activeTopic.answered_by_name}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-700">Ainda nao ha resposta oficial para esta duvida.</p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Comentarios</h3>
                  <div className="space-y-3">
                    {comments.length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum comentario ainda.</p>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-600">{comment.username}</p>
                            <p className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleString('pt-BR')}</p>
                          </div>
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                            {comment.content || 'Comentario removido'}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Adicionar comentario</h3>
                  <textarea
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Compartilhe sua resposta ou duvida adicional..."
                  />
                  <button
                    onClick={handleCreateComment}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    <FiSend />
                    Enviar comentario
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Tutoring;
